/**
 * Admin → Ask ABHI client: usage, per-user activity and the review queue.
 *
 * Mirrors hooten-young-sales src/hy_sales/schemas/admin_chat.py.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ChatCitation } from './chat';
import { apiGet, apiPost } from './client';

export type ReviewKind = 'all' | 'down' | 'up' | 'withheld' | 'error' | 'rewritten' | 'rated';

export interface ChatDayUsage {
  day: string;
  questions: number;
  answered: number;
  refused: number;
  withheld: number;
  errors: number;
}

export interface ChatOverview {
  days: number;
  questions: number;
  conversations: number;
  active_users: number;
  answered: number;
  clarify: number;
  refused: number;
  withheld: number;
  errors: number;
  thumbs_up: number;
  thumbs_down: number;
  unreviewed_down: number;
  /** Needs attention (rated down, held back, errored, rewritten) and not yet reviewed. */
  needs_review: number;
  /** Answers that passed only after a rewrite, in the period. */
  rewritten: number;
  avg_latency_ms: number | null;
  median_latency_ms: number | null;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
  cost_per_question_usd: number | null;
  cost_month_pace_usd: number;
  cost_estimated: boolean;
  daily: ChatDayUsage[];
  reasons: { reason: string; count: number }[];
  /** Question types, from the data lookups behind each answer. */
  topics: ChatTopicCount[];
  top_states: ChatTopicCount[];
  top_products: ChatTopicCount[];
  /** Recent questions Ask ABHI turned down — unmet demand. */
  out_of_scope_examples: string[];
}

export interface ChatTopicCount {
  label: string;
  count: number;
  /** Filter key for the question list: topic key, state code or product id. */
  key: string | null;
  /** Count in the previous period of the same length. */
  previous: number | null;
  thumbs_up: number;
  thumbs_down: number;
  held_back: number;
}

export interface ChatQuestionItem {
  asked_at: string;
  user: { id: string; name: string; email: string };
  question: string;
  conversation_id: number;
  answer_id: number | null;
  status: string | null;
  rating: number | null;
  latency_ms: number | null;
  /** Question types (same rules as the Topics tab) and what it was about. */
  topics: string[];
  states: string[];
  products: string[];
  reasons: string[];
  comment: string | null;
  /** 0 = first question in its chat; n = the n-th follow-up. */
  follow_up_number: number;
  sources: number;
  cost_usd: number | null;
  cost_estimated: boolean;
  reviewed: boolean;
  /** First readable line of the answer shown. */
  answer_preview: string | null;
  /** Times this exact question was asked in the period, by anyone. */
  asked_times: number;
  /** Rewrites the number check forced. */
  rewrites: number;
}

export type QuestionFilter =
  | 'all'
  | 'rated'
  | 'down'
  | 'up'
  | 'held_back'
  | 'out_of_scope'
  | 'errors'
  | 'follow_ups';

export interface ChatQuestionPage {
  items: ChatQuestionItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface ChatUserUsage {
  user_id: string;
  name: string;
  email: string;
  roles: string[];
  questions: number;
  conversations: number;
  last_active: string;
  thumbs_up: number;
  thumbs_down: number;
  withheld: number;
  errors: number;
  out_of_scope: number;
  /** Questions per day across the period, oldest first. */
  daily: number[];
  active_days: number;
  first_used: string | null;
  top_topics: ChatTopicCount[];
  top_subjects: ChatTopicCount[];
  /** State names they asked about. */
  states_asked: string[];
  /** Field territory, as state names. */
  territory: string[];
  cost_usd: number;
  cost_estimated: boolean;
}

/** Someone with access who hasn't asked anything yet. */
export interface ChatNotStarted {
  user_id: string;
  name: string;
  email: string;
  roles: string[];
  last_login_at: string | null;
}

export interface ChatReviewItem {
  message_id: number;
  conversation_id: number;
  created_at: string;
  user: { id: string; name: string; email: string };
  question: string | null;
  answer_preview: string | null;
  status: string | null;
  rating: number | null;
  reasons: string[];
  comment: string | null;
  feedback_at: string | null;
  latency_ms: number | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  review_note: string | null;
  topics: string[];
  /** Figures that failed the number check, and the closest real values. */
  unmatched_numbers: string[];
  nearest: Record<string, string>;
  rewrites: number;
}

export interface ChatReviewPage {
  items: ChatReviewItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface ChatToolCall {
  label: string;
  tool: string;
  input: Record<string, unknown>;
  rows: number | null;
  ms: number | null;
  failed: boolean;
}

export interface ChatReviewDetail extends ChatReviewItem {
  answer: string;
  citations: ChatCitation[];
  tool_calls: ChatToolCall[];
  numbers_checked: number;
  unmatched_numbers: string[];
  retries: number;
  rejected_drafts: { text: string; problems: string[] }[];
  model: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  earlier: { role: 'user' | 'assistant'; content: string; created_at: string }[];
}

export interface ReviewFilters {
  kind: ReviewKind;
  reviewed?: boolean;
  user_id?: string;
  days: number;
  limit: number;
  offset: number;
}

const BASE = '/api/admin/ask-abhi';
const KEY = ['admin', 'ask-abhi'] as const;

export function useChatOverview(days: number) {
  return useQuery({
    queryKey: [...KEY, 'overview', days],
    queryFn: () => apiGet<ChatOverview>('sales', `${BASE}/overview`, { days }),
    placeholderData: keepPreviousData,
  });
}

export function useChatUsers(days: number) {
  return useQuery({
    queryKey: [...KEY, 'users', days],
    queryFn: () => apiGet<ChatUserUsage[]>('sales', `${BASE}/users`, { days }),
    placeholderData: keepPreviousData,
  });
}

export function useChatNotStarted() {
  return useQuery({
    queryKey: [...KEY, 'not-started'],
    queryFn: () => apiGet<ChatNotStarted[]>('sales', `${BASE}/not-started`),
  });
}

export function useChatQuestions(f: {
  user_id?: string;
  search?: string;
  result?: QuestionFilter;
  topic?: string;
  state?: string;
  product_id?: number;
  days: number;
  limit: number;
  offset: number;
}) {
  return useQuery({
    queryKey: [...KEY, 'questions', f],
    queryFn: () =>
      apiGet<ChatQuestionPage>('sales', `${BASE}/questions`, {
        user_id: f.user_id,
        search: f.search || undefined,
        result: f.result && f.result !== 'all' ? f.result : undefined,
        topic: f.topic,
        state: f.state,
        product_id: f.product_id,
        days: f.days,
        limit: f.limit,
        offset: f.offset,
      }),
    placeholderData: keepPreviousData,
  });
}

export function useChatReviewQueue(f: ReviewFilters) {
  return useQuery({
    queryKey: [...KEY, 'messages', f],
    queryFn: () =>
      apiGet<ChatReviewPage>('sales', `${BASE}/messages`, {
        kind: f.kind,
        reviewed: f.reviewed === undefined ? undefined : String(f.reviewed),
        user_id: f.user_id,
        days: f.days,
        limit: f.limit,
        offset: f.offset,
      }),
    placeholderData: keepPreviousData,
  });
}

export function useChatReviewDetail(id: number | null) {
  return useQuery({
    queryKey: [...KEY, 'message', id],
    queryFn: () => apiGet<ChatReviewDetail>('sales', `${BASE}/messages/${id}`),
    enabled: id !== null,
  });
}

export function useMarkReviewed() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: number; reviewed: boolean; note?: string }) =>
      apiPost<ChatReviewItem>('sales', `${BASE}/messages/${v.id}/review`, {
        reviewed: v.reviewed,
        note: v.note,
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

/** Labels for the thumbs-down reasons (values match ChatFeedbackReason). */
export const REASON_LABELS: Record<string, string> = {
  wrong_numbers: 'Numbers Look Wrong',
  not_what_i_asked: "Didn't Answer the Question",
  missing_data: 'Missing Data',
  hard_to_read: 'Hard to Read',
  too_slow: 'Too Slow',
  other: 'Other',
};
